/* src/ui/connStatus.ts — the live Supabase connection probe (TASKS.md 0.11).
 *
 * This component is what makes Phase 0's Definition of Done visible: a human loads the app
 * and sees, on screen, whether the client actually reached the database. It is also the first
 * real exercise of every Phase 0 primitive — el(), Component, the event bus, and the root
 * action dispatcher (the Retry button goes through data-action, not a listener).
 *
 * The probe is a `head: true, count: 'exact'` select against `players`. With no session, RLS
 * default-deny (migration 0001) returns HTTP 200 and a count of 0 — the result we want, because
 * it proves three things at once: the URL resolves, the anon key is valid, and RLS is switched
 * on. More rows than the caller is entitled to would mean the table is readable by the world,
 * and is reported here as a failure, not a success.
 *
 * How many rows the caller is entitled to changed in Phase 2, when anonymous sign-in landed —
 * see the note at the call site. `scripts/verify-rls.mjs` covers the properties this probe
 * structurally cannot: cross-player isolation, and that direct writes are refused.
 */

import { emit } from '../core/events.ts';
import type { ConnState } from '../core/types.ts';
import { getSupabase } from '../net/supabaseClient.ts';
import { Component } from './component.ts';
import { el, text } from './dom.ts';
import { registerAction } from './dispatch.ts';

const PHASE_LABEL: Record<ConnState['phase'], string> = {
  idle: 'Idle',
  connecting: 'Connecting…',
  online: 'Online',
  unconfigured: 'Not configured',
  error: 'Unreachable',
};

export class ConnStatus extends Component<void> {
  #label!: Text;
  #detail!: Text;
  #retry!: HTMLElement;
  #state: ConnState = { phase: 'idle', detail: '', latencyMs: null };

  constructor() {
    super(undefined);
  }

  protected render(): HTMLElement {
    // The dot needs no JS handle — CSS drives its colour off the root's `is-*` state class.
    const dot = el('span', { class: 'gf-conn-dot', attrs: { 'aria-hidden': 'true' } });
    this.#label = text(PHASE_LABEL.idle);
    this.#detail = text('');
    this.#retry = el(
      'button',
      {
        class: 'gf-conn-retry',
        attrs: { type: 'button' },
        data: { action: 'conn:retry' },
      },
      'Retry',
    );

    return el(
      'div',
      {
        class: 'gf-conn',
        // Screen readers should hear the result change, but not be interrupted mid-sentence.
        attrs: { role: 'status', 'aria-live': 'polite' },
      },
      el('div', { class: 'gf-conn-head' }, dot, el('span', { class: 'gf-conn-label' }, this.#label)),
      el('p', { class: 'gf-conn-detail' }, this.#detail),
      this.#retry,
    );
  }

  protected override onMount(): void {
    this.onCleanup(registerAction('conn:retry', () => void this.probe()));
    void this.probe();
  }

  #apply(next: ConnState): void {
    this.#state = next;
    const root = this.el;

    for (const phase of Object.keys(PHASE_LABEL) as Array<ConnState['phase']>) {
      root.classList.toggle(`is-${phase}`, phase === next.phase);
    }
    this.#label.textContent =
      next.phase === 'online' && next.latencyMs !== null
        ? `${PHASE_LABEL.online} · ${Math.round(next.latencyMs)} ms`
        : PHASE_LABEL[next.phase];
    this.#detail.textContent = next.detail;
    this.#retry.hidden = next.phase === 'connecting' || next.phase === 'online';

    emit('conn:change', next);
  }

  get state(): ConnState {
    return this.#state;
  }

  async probe(): Promise<void> {
    const supabase = getSupabase();

    if (!supabase) {
      this.#apply({
        phase: 'unconfigured',
        detail:
          'No Supabase credentials. Copy .env.example to .env, set VITE_SUPABASE_URL and ' +
          'VITE_SUPABASE_ANON_KEY, apply supabase/migrations/0001_init.sql, and restart the dev server.',
        latencyMs: null,
      });
      return;
    }

    this.#apply({ phase: 'connecting', detail: 'Querying the players table…', latencyMs: null });

    /*
     * Whether a row is expected depends on who is asking, and from Phase 2 that is no longer
     * "nobody". `signInAnonymously()` produces a real session with the **`authenticated`**
     * role — Supabase marks it `is_anonymous: true` in the JWT, but it is not the `anon` role —
     * so the caller legitimately sees exactly their own player row.
     *
     * The original probe assumed no session could ever exist and reported any row as an RLS
     * breach. That was correct in Phase 0 and became a false alarm the moment sign-in landed:
     * it spent a while on screen accusing a perfectly sound database of leaking. A check that
     * cries wolf is worse than no check, because it trains you to ignore the real one.
     *
     * So: expect 0 rows without a session, exactly 1 with one. `scripts/verify-rls.mjs` covers
     * what this cannot — that one player cannot read another's rows even knowing their id, and
     * that a direct PostgREST write is refused.
     */
    const { data: sessionData } = await supabase.auth.getSession();
    const signedIn = sessionData.session !== null;

    const started = performance.now();
    const { count, error } = await supabase
      .from('players')
      .select('id', { count: 'exact', head: true });
    const latencyMs = performance.now() - started;

    if (error) {
      // Deliberately surfaced verbatim: at this stage the message ("relation does not exist",
      // "Invalid API key") is the fastest route to the actual misconfiguration.
      this.#apply({ phase: 'error', detail: error.message, latencyMs: null });
      return;
    }

    const visible = count ?? 0;
    const expected = signedIn ? 1 : 0;

    if (visible > expected) {
      this.#apply({
        phase: 'error',
        detail:
          `Reached the database, but ${signedIn ? 'this player' : 'an anonymous caller'} can read ` +
          `${String(visible)} players row(s) — expected ${String(expected)}. RLS is not enforcing ` +
          'default-deny; run `node scripts/verify-rls.mjs` to see which policy is missing.',
        latencyMs: null,
      });
      return;
    }

    this.#apply({
      phase: 'online',
      detail: signedIn
        ? `Signed in; RLS scopes the players table to this player only (${String(visible)} row).`
        : 'Reached the players table over the anon key; RLS returned 0 rows, as it should.',
      latencyMs,
    });
  }
}
